/**
 * Seat guid handling, shared by the picker, the checkout wizard and the administrator
 * seating screens.
 *
 * Seat identity is a free-text string inside an opaque layout JSON, and the layouts mix
 * case (`main-A-22`). The backend stores whatever the layout spells, and since it now
 * rewrites a submitted guid to the layout's own spelling, what a client sent is not
 * necessarily what comes back on the ticket. So any comparison between a locally held
 * guid and one that came from the server has to be case-insensitive, while the value
 * actually sent to the API stays byte-for-byte what the layout said.
 *
 * `seatKey` is the comparison key and never the stored value. It mirrors
 * `SeatMapCategoryReader.key` on the backend, deliberately, so both sides group the same
 * two spellings together.
 */

/** The comparison key for a seat guid: trimmed and lower-cased. Never store this. */
export function seatKey(rawSeatGuid: unknown): string {
    return String(rawSeatGuid ?? "").trim().toLowerCase();
}

/** True when two guids name the same physical seat, whatever their spelling. */
export function sameSeat(a: unknown, b: unknown): boolean {
    const ka = seatKey(a);
    return ka.length > 0 && ka === seatKey(b);
}

/** A set of comparison keys, for `has` checks against a list of guids from the server. */
export function seatKeySet(guids: Iterable<unknown> | null | undefined): Set<string> {
    const set = new Set<string>();
    for (const g of guids ?? []) {
        const k = seatKey(g);
        if (k) set.add(k);
    }
    return set;
}

/**
 * A seat rendered for a human, from the layout's guid. Mirrors `FormatUtils.seatLabel` on
 * the backend, so a label derived here for a seat the API never mentioned (a free seat on
 * the admin seating map, say) reads the same as one the API sends for a sold seat.
 *
 * A guid with fewer than three dash-separated parts is returned as it is, because there is
 * no row and seat to read out of it.
 */
export function seatLabel(rawSeatGuid: unknown): string {
    const guid = String(rawSeatGuid ?? "").trim();
    if (!guid) return "\u2014";
    const parts = guid.split("-");
    if (parts.length < 3) return guid;
    return `Row ${parts[1]} Seat ${parts[2]}`;
}
